import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "./ctx";
import { MaskLines, Reveal, Label } from "./Motion";

const GradeRow = ({ g, i, open, onToggle }) => (
  <div className="border-t border-stone-300 last:border-b" data-testid={`kaolin-grade-card-${i + 1}`}>
    <button onClick={onToggle} data-testid={`kaolin-grade-toggle-${i + 1}`} className="group flex w-full items-center gap-4 py-6 text-left sm:gap-6 sm:py-7">
      <span className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
        <span className="shrink-0 font-mono text-[11px] tracking-[0.2em] text-[#A8874F] sm:w-20">{g.code}</span>
        <span className="font-display text-2xl font-light text-stone-950 transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl">{g.name}</span>
      </span>
      <Plus size={20} strokeWidth={1.25} className={`shrink-0 text-stone-500 transition-transform duration-500 ${open ? "rotate-45" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
      <p className="pb-7 pl-0 text-sm sm:text-base leading-relaxed text-stone-600 sm:pl-[6.5rem]">{g.d}</p>
    </motion.div>
  </div>
);

export const Kaolin = () => {
  const { t, lang } = useLang();
  const k = t.kaolin;
  const [open, setOpen] = useState(0);

  return (
    <section id="kaolin" className="relative bg-[#F2EFE8] py-20 sm:py-28 lg:py-40" data-testid="kaolin-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Label>{k.label}</Label>
            <h2 key={lang} className="mt-8 font-display text-5xl font-light leading-[0.98] tracking-tight text-stone-950 sm:text-7xl lg:text-8xl">
              <MaskLines lines={[k.title[0]]} />
              <MaskLines lines={[k.title[1]]} delay={0.12} lineClass="italic text-[#A8874F]" />
            </h2>
          </div>
          <Reveal className="lg:col-span-5 lg:pt-24">
            <p className="text-base leading-relaxed text-stone-700 md:text-lg">{k.intro}</p>
          </Reveal>
        </div>

        <div className="mt-12 grid gap-4 sm:mt-20 sm:gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="spotlight relative h-full min-h-[300px] overflow-hidden sm:min-h-[420px] rounded-sm bg-white">
              <img src="/assets/powder.jpg" alt="Kaolin powder" className="absolute inset-0 h-full w-full scale-125 object-cover transition-transform duration-[2s] ease-out hover:scale-[1.35]" data-testid="kaolin-image" />
              <div className="absolute left-6 top-6 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">Kaolin · Sample 01</div>
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <span className="font-display text-5xl font-light italic text-stone-900/90 sm:text-7xl">Al₂O₃</span>
                <span className="mb-3 h-10 w-10 border-b border-r border-[#A8874F]" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="h-full rounded-sm border border-stone-300/80 bg-[#FBFAF7] p-5 sm:p-8 lg:p-10" data-testid="kaolin-specs-table">
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-xl text-stone-950 sm:text-2xl">{k.specsTitle}</h3>
                <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 sm:inline">Typ.</span>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-px sm:mt-8 overflow-hidden bg-stone-200 sm:grid-cols-3">
                {k.specs.map(([a, b]) => (
                  <div key={a} className="group bg-[#FBFAF7] p-4 transition-colors duration-300 hover:bg-white sm:p-5">
                    <div className="font-mono text-[11px] tracking-[0.15em] text-stone-500">{a}</div>
                    <div className="mt-3 whitespace-nowrap font-display text-lg text-stone-950 transition-colors duration-300 group-hover:text-[#A8874F] sm:text-3xl">{b}</div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs leading-relaxed text-stone-500">{k.specsNote}</p>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-12 sm:mt-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {k.grades.map((g, i) => (
              <GradeRow key={g.code} g={g} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
            ))}
          </div>
          <Reveal className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-stone-500">{k.usesTitle}</div>
            <div className="mt-6 flex flex-wrap gap-2.5" data-testid="kaolin-uses">
              {k.uses.map((u) => (
                <span key={u} className="rounded-full border border-stone-300 bg-[#FBFAF7] px-4 py-2 text-sm text-stone-800 transition-colors duration-300 hover:border-[#A8874F] hover:text-[#8A6D3B]">{u}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
